import { useCallback, useState, useEffect } from "react";
import { useAudioPlayer, useAudioPlayerStatus } from "expo-audio";
import { logger } from "@/utils/logger";

export interface VoiceSamplePlayer {
  play: (audioFile: string) => void;
  stop: () => void;
  isPlaying: boolean;
}

/**
 * Hook for playing voice sample audio files
 * Uses expo-audio for cross-platform audio playback (web, iOS, Android)
 */
export function useVoiceSamplePlayer(): VoiceSamplePlayer {
  const player = useAudioPlayer(null);
  const status = useAudioPlayerStatus(player);

  const [isPlaying, setIsPlaying] = useState(false);

  // Listen for playback events to clear loading state
  useEffect(() => {
    if (status?.didJustFinish) {
      logger.debug("Audio finished");
      setIsPlaying(false);
    }
  }, [status]);

  const play = useCallback(
    (audioFile: string) => {
      setIsPlaying(true);

      try {
        player.pause(); // stop current immediately
        logger.debug("Audio loading: ", audioFile);
        player.replace({ uri: audioFile });
        player.play();
      } catch (error) {
        logger.error("Failed to play audio:", error);
        setIsPlaying(false);
      }
    },
    [player]
  );

  const stop = useCallback(() => {
    if (isPlaying) {
      player.pause();
      setIsPlaying(false);
    }
  }, [player, isPlaying]);

  return {
    play,
    stop,
    isPlaying
  };
}
